import type { PortalCommentPage } from '@/modules/client-portal/discussion-read-window'
import type { ThreadFilters } from '@/store/thread-filters.store'
import { useMemo } from 'react'
import {
  isThreadReadUnavailable,
  threadReadsApi,
} from '@/modules/threads/reads.api'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import type {
  ModuleTypeEnum,
  PaginatedResponse,
  Thread,
  ThreadComment,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import {
  executeNotificationReader,
  isNotificationReaderRefused,
  refuseNotificationReader,
} from './notification-reader-cache'
import { useThreadCommentReaderRealtime } from './useThreadCommentReaderRealtime'
import { useThreadReadSession } from './useThreadReadSession'

export type { PaginatedResponse }
const THREADS_STALE_TIME = 60 * 1000
const THREADS_GC_TIME = 10 * 60 * 1000
const THREAD_COMMENTS_STALE_TIME = 2 * 60 * 1000
const THREADS_PAGE_SIZE = 30
const EMPTY_THREADS: Thread[] = []
const EMPTY_COMMENTS: ThreadComment[] = []
export interface ThreadCommentSeed {
  page: PaginatedResponse<ThreadComment>
  sessionKey: ReturnType<typeof useThreadReadSession>['key']
  updatedAt: number
}

export function useModuleCommentsQuery(
  params: { moduleType: ModuleTypeEnum; moduleId: string; moduleName?: string },
  enabled: boolean,
) {
  const session = useThreadReadSession()
  const active = enabled && session.staffEnabled && !!params.moduleId
  const query = useQuery({
    queryKey: [
      ...queryKeys.threads.all,
      'module-comments',
      params.moduleType,
      params.moduleId,
      params.moduleName,
      session.key,
    ],
    queryFn: ({ signal }) =>
      session.run(signal, context =>
        threadReadsApi.moduleComments(params, context),
      ),
    enabled: active,
    staleTime: THREAD_COMMENTS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
    refetchOnWindowFocus: false,
  })
  const data =
    active && !isThreadReadUnavailable(query.error) ? query.data : undefined
  return {
    ...query,
    data,
    commentSeed: data
      ? {
          page: data.page,
          sessionKey: session.key,
          updatedAt: query.dataUpdatedAt,
        }
      : undefined,
  }
}

/** Prefixes stay compatible with existing invalidation, while every reader owns
 * its account/access session and client route. No previous-scope placeholders.
 */
export function useThreadsQuery(
  filters?: ThreadFilters,
  options?: { enabled?: boolean },
) {
  const session = useThreadReadSession()
  const enabled = options?.enabled !== false && session.staffEnabled
  const query = useQuery<PaginatedResponse<Thread>>({
    queryKey: [...queryKeys.threads.list(filters), session.key],
    queryFn: ({ signal }) =>
      session.run(signal, context =>
        threadReadsApi.list(filters ?? {}, context),
      ),
    enabled,
    staleTime: THREADS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  })
  return {
    ...query,
    data:
      enabled && !isThreadReadUnavailable(query.error) ? query.data : undefined,
  }
}

type ThreadQueryFilters = Pick<
  ThreadFilters,
  | 'title'
  | 'sortBy'
  | 'sortOrder'
  | 'moduleType'
  | 'moduleId'
  | 'threadType'
  | 'membershipOnly'
  | 'excludeComments'
>
export function useInfiniteThreadsQuery(
  filters?: ThreadQueryFilters,
  options?: { enabled?: boolean },
) {
  const session = useThreadReadSession()
  const enabled = options?.enabled !== false && session.staffEnabled
  const query = useInfiniteQuery<PaginatedResponse<Thread>>({
    queryKey: [
      ...queryKeys.threads.all,
      'infinite',
      {
        threadType: filters?.threadType ?? 'all',
        membershipOnly: filters?.membershipOnly,
        excludeComments: filters?.excludeComments,
        moduleType: filters?.moduleType,
        moduleId: filters?.moduleId,
        title: filters?.title,
        sortBy: filters?.sortBy,
        sortOrder: filters?.sortOrder,
      },
      session.key,
    ],
    queryFn: ({ signal, pageParam = 1 }) =>
      session.run(signal, context =>
        threadReadsApi.list(
          { ...filters, page: pageParam as number, limit: THREADS_PAGE_SIZE },
          context,
        ),
      ),
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.meta.hasNextPage ? lastPage.meta.currentPage + 1 : undefined,
    enabled,
    staleTime: THREADS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
  })
  const data =
    enabled && !isThreadReadUnavailable(query.error) ? query.data : undefined
  // Keep the flattened list stable across unrelated renders and empty states.
  const threads = useMemo(
    () => data?.pages.flatMap(page => page.data) ?? EMPTY_THREADS,
    [data],
  )
  return { ...query, data, threads }
}

/** Prefetch uses the visible list's filters, key and immutable read lease. */
export function usePrefetchThreads() {
  const client = useQueryClient()
  const session = useThreadReadSession()
  return async (filters: ThreadFilters) => {
    if (!session.staffEnabled) return
    await client.prefetchQuery({
      queryKey: [...queryKeys.threads.list(filters), session.key],
      queryFn: ({ signal }) =>
        session.run(signal, context => threadReadsApi.list(filters, context)),
      staleTime: THREADS_STALE_TIME,
    })
  }
}

export function useThreadQuery(
  id: string | undefined,
  options?: { enabled?: boolean },
) {
  const session = useThreadReadSession()
  const enabled = options?.enabled !== false && !!id && session.enabled
  const query = useQuery<Thread>({
    queryKey: [...queryKeys.threads.detail(id ?? ''), session.key],
    queryFn: ({ signal }) =>
      session.run(signal, context => threadReadsApi.detail(id!, context)),
    enabled,
    staleTime: THREADS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
  return {
    ...query,
    data:
      enabled && !isThreadReadUnavailable(query.error) ? query.data : undefined,
  }
}

/** Publish a module's discussion digest only after every bounded page succeeds. */
export function useThreadsByModuleQuery(
  moduleType: ModuleTypeEnum | undefined,
  moduleId: string | undefined,
  options?: {
    enabled?: boolean
    threadType?: string
    membershipOnly?: boolean
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  },
) {
  const session = useThreadReadSession()
  const enabled =
    options?.enabled !== false &&
    !!moduleType &&
    !!moduleId &&
    session.staffEnabled
  const filters = {
    moduleType,
    moduleId,
    threadType: options?.threadType,
    membershipOnly: options?.membershipOnly,
    sortBy: options?.sortBy,
    sortOrder: options?.sortOrder,
  }
  const query = useQuery<Thread[]>({
    queryKey: [
      ...queryKeys.threads.all,
      'module',
      moduleType,
      moduleId,
      {
        threadType: options?.threadType,
        membershipOnly: options?.membershipOnly,
        sortBy: options?.sortBy,
        sortOrder: options?.sortOrder,
      },
      session.key,
    ],
    queryFn: ({ signal }) =>
      session.run(signal, context => threadReadsApi.module(filters, context)),
    enabled,
    staleTime: THREADS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
  })
  return {
    ...query,
    data:
      enabled && !isThreadReadUnavailable(query.error) ? query.data : undefined,
  }
}

/** Existing five-minute unread cache, without focus polling. */
export function useUnreadThreadsCount(options?: {
  enabled?: boolean
  threadType?: string
}) {
  const session = useThreadReadSession()
  const enabled = options?.enabled !== false && session.staffEnabled
  const query = useQuery<number>({
    queryKey: [
      ...queryKeys.threads.unreadCount(options?.threadType),
      session.key,
    ],
    queryFn: ({ signal }) =>
      session.run(signal, context =>
        threadReadsApi.unreadCount(options?.threadType, context),
      ),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: THREADS_GC_TIME,
    refetchOnWindowFocus: false,
  })
  return {
    ...query,
    data:
      enabled && !isThreadReadUnavailable(query.error) ? query.data : undefined,
  }
}

export function useThreadCommentsQuery(
  threadId: string | undefined,
  options?: {
    parentCommentId?: string | null
    limit?: number
    page?: number
    enabled?: boolean
    sortOrder?: 'asc' | 'desc'
  },
) {
  const session = useThreadReadSession()
  const enabled = options?.enabled !== false && !!threadId && session.enabled
  const query = useQuery<PaginatedResponse<ThreadComment>>({
    queryKey: [
      ...queryKeys.threads.detail(threadId ?? ''),
      'comments',
      options?.parentCommentId,
      options?.page ?? 1,
      options?.limit ?? 10,
      options?.sortOrder ?? 'asc',
      session.key,
    ],
    queryFn: ({ signal }) =>
      session.run(signal, context =>
        threadReadsApi.comments(
          {
            threadId: threadId!,
            parentCommentId: options?.parentCommentId,
            limit: options?.limit ?? 10,
            page: options?.page ?? 1,
            sortOrder: options?.sortOrder ?? 'asc',
          },
          context,
        ),
      ),
    enabled,
    staleTime: THREAD_COMMENTS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
  return {
    ...query,
    data:
      enabled && !isThreadReadUnavailable(query.error) ? query.data : undefined,
  }
}

/** Fixed pages replace the growing-limit requests that downloaded old rows
 * again. This namespace cannot be populated by legacy socket writer upserts.
 */
export function useInfiniteThreadCommentsQuery(
  threadId: string | undefined,
  options: {
    parentCommentId: string | null
    enabled?: boolean
    seed?: ThreadCommentSeed
  },
) {
  const session = useThreadReadSession()
  const enabled = options.enabled !== false && !!threadId && session.enabled
  const client = useQueryClient()
  const seed =
    !session.key.portalEntityId &&
    options.parentCommentId === null &&
    options.seed &&
    JSON.stringify(options.seed.sessionKey) === JSON.stringify(session.key)
      ? options.seed
      : undefined
  const queryKey = [
    ...queryKeys.threads.detail(threadId ?? ''),
    'reader-comments',
    options.parentCommentId,
    10,
    'desc',
    session.key,
  ]
  const query = useInfiniteQuery<PortalCommentPage>({
    queryKey,
    queryFn: ({ signal, pageParam }) =>
      executeNotificationReader(
        client,
        queryKey,
        session,
        signal,
        async context => {
          try {
            return await threadReadsApi.comments(
              {
                threadId: threadId!,
                parentCommentId: options.parentCommentId,
                page: pageParam as number,
                limit: 10,
                sortOrder: 'desc',
              },
              context,
            )
          } catch (error) {
            if (isThreadReadUnavailable(error))
              refuseNotificationReader(client, queryKey)
            throw error
          }
        },
        false,
      ),
    initialPageParam: 1,
    getNextPageParam: last =>
      last.meta.hasNextPage ? last.meta.currentPage + 1 : undefined,
    initialData: seed ? { pages: [seed.page], pageParams: [1] } : undefined,
    initialDataUpdatedAt: seed?.updatedAt,
    enabled,
    staleTime: THREAD_COMMENTS_STALE_TIME,
    gcTime: THREADS_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
  useThreadCommentReaderRealtime(threadId, queryKey, session, enabled)
  const unavailable =
    isThreadReadUnavailable(query.error) ||
    isNotificationReaderRefused(client, queryKey)
  const data = enabled && !unavailable ? query.data : undefined
  const comments = useMemo(
    () => data?.pages.flatMap(page => page.data) ?? EMPTY_COMMENTS,
    [data],
  )
  const refreshComments = () => {
    try {
      session.assertCurrent()
    } catch {
      return Promise.resolve()
    }
    // A root visibility change cascades to replies. Refresh this reader family
    // without borrowing the command response or reviving another account's cache.
    return client.invalidateQueries({
      queryKey: [
        ...queryKeys.threads.detail(threadId ?? ''),
        'reader-comments',
      ],
      predicate: candidate =>
        JSON.stringify(candidate.queryKey.at(-1)) ===
        JSON.stringify(session.key),
    })
  }
  return {
    ...query,
    data,
    comments,
    unavailable,
    enabled,
    refreshComments,
    queryKey,
    owner: session.owner,
  }
}
